import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SSLStatusIndicator from '../components/SSLStatusIndicator';
import RightClickProtection from '../components/RightClickProtection';
import ScreenshotProtection from '../components/ScreenshotProtection';

const {FiExternalLink,FiMessageCircle,FiArrowRight,FiAlertTriangle,FiUsers,FiLock,FiCheck,FiZap,FiChevronRight,FiActivity}=FiIcons;

function LandingPage() {
  // Einfache Tracking Funktion zur Simulation in der Konsole
  const trackClick = (cta_name) => {
    console.log(`Tracking Click: ${cta_name}`);
  };

  const handleChatGPTClick = () => {
    trackClick('chatgpt_link');
    window.open('https://chatgpt.com/', '_blank');
  };

  const handleCommunityClick = () => {
    trackClick('community_link');
    window.open('https://monkeymedia.at/go/kicommunity', '_blank');
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-orange-500/5 via-transparent to-orange-500/10"></div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl"></div>
      
      {/* Protection Components */}
      <RightClickProtection />
      <ScreenshotProtection />
      <SSLStatusIndicator />
      
      {/* Header */}
      <header className="relative z-10 bg-black/50 backdrop-blur-xl border-b border-white/10 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <motion.div className="flex items-center gap-4" initial={{opacity: 0,x: -30}} animate={{opacity: 1,x: 0}} transition={{duration: 0.8,ease: "easeOut"}}>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiMessageCircle} className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-black tracking-tight text-white uppercase">
                  CHATTY
                </h1>
                <p className="text-xs lg:text-sm text-orange-400 font-bold uppercase tracking-wider">
                  ist dein FREUND
                </p>
              </div>
            </motion.div>
            
            <motion.nav className="hidden lg:flex items-center gap-8" initial={{opacity: 0,x: 30}} animate={{opacity: 1,x: 0}} transition={{duration: 0.8,delay: 0.2,ease: "easeOut"}}>
              <button onClick={handleChatGPTClick} className="text-white/80 hover:text-white font-bold text-sm uppercase tracking-wider transition-all hover:text-orange-400 flex items-center gap-2">
                <SafeIcon icon={FiMessageCircle} className="text-lg" />
                ChatGPT
              </button>
              <button onClick={handleCommunityClick} className="text-white/80 hover:text-white font-bold text-sm uppercase tracking-wider transition-all hover:text-orange-400 flex items-center gap-2">
                <SafeIcon icon={FiUsers} className="text-lg" />
                Community
              </button>
            </motion.nav>

            {/* Mobile CTA */}
            <motion.div className="lg:hidden" initial={{opacity: 0,scale: 0.9}} animate={{opacity: 1,scale: 1}} transition={{duration: 0.8}}>
              <button onClick={handleChatGPTClick} className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-3 rounded-full text-sm font-black uppercase tracking-wider">
                ChatGPT
              </button>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex-grow">
        {/* Hero Section - Nike Style */}
        <section className="min-h-screen flex items-center justify-center px-6 lg:px-8">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Column - Hero Message */}
            <motion.div className="space-y-8" initial={{opacity: 0,y: 50}} animate={{opacity: 1,y: 0}} transition={{duration: 1,ease: "easeOut"}}>
              <div className="space-y-6">
                
                
                <h2 className="text-5xl lg:text-7xl font-black text-white leading-none">
                  <span className="block">Du hast es</span>
                  <span className="block text-orange-500">geschafft.</span>
                </h2>
                
                <p className="text-xl lg:text-2xl text-white/80 font-light leading-relaxed">
                  Jemand mit einer unnötigen Frage zu ärgern. 
                  <span className="text-orange-400 font-bold"> Echt jetzt?</span>
                </p>
              </div>

              <motion.div 
                className="bg-gradient-to-r from-orange-500/20 to-red-600/20 border-l-4 border-orange-500 p-8 rounded-r-2xl backdrop-blur-sm"
                initial={{opacity: 0,x: -30}}
                animate={{opacity: 1,x: 0}}
                transition={{duration: 0.8,delay: 0.3}}
              >
                <p className="text-2xl lg:text-3xl font-black text-orange-400 uppercase tracking-tight">
                  Nutze verdammt nochmal<br/>ChatGPT!
                </p>
              </motion.div>

              <div className="space-y-4">
                <p className="text-lg text-white/60 italic font-light">
                  Da du diesem Link gefolgt bist, bestätigt das wirklich, dass du keine Ahnung hast!
                </p>
                <p className="text-xl font-bold text-orange-400 uppercase tracking-wider">
                  Alle Internetuser hoffen, dass du was gelernt hast!
                </p>
              </div>

              
            </motion.div>

            {/* Right Column - Interactive Demo */}
            <motion.div 
              className="relative"
              initial={{opacity: 0,scale: 0.9}}
              animate={{opacity: 1,scale: 1}}
              transition={{duration: 1,delay: 0.4,ease: "easeOut"}}
            >
              <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-red-600 to-orange-500"></div>
                <div className="p-8 lg:p-12">
                  <motion.div 
                    className="bg-black/50 backdrop-blur-sm rounded-2xl p-8 border border-white/10 cursor-pointer transition-all hover:bg-black/70 hover:scale-105 hover:border-orange-500/30"
                    onClick={handleChatGPTClick}
                    whileHover={{scale: 1.02}}
                    whileTap={{scale: 0.98}}
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center">
                        <SafeIcon icon={FiMessageCircle} className="text-white text-2xl" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-black text-white uppercase tracking-tight">ChatGPT</h3>
                        <p className="text-orange-400 font-bold text-sm uppercase tracking-wider">AI Assistant</p>
                      </div>
                    </div>
                    
                    <p className="text-white/80 text-lg mb-6 font-light leading-relaxed">
                      Ich bin ChatGPT. Ich kann dir bei Fragen helfen, Texte schreiben, Probleme lösen und vieles mehr. Was kann ich heute für dich tun?
                    </p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                        <p className="text-orange-400 font-bold text-sm uppercase tracking-wider mb-2">Schnell</p>
                        <p className="text-white/60 text-sm">Sofortige Antworten</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                        <p className="text-orange-400 font-bold text-sm uppercase tracking-wider mb-2">24/7</p>
                        <p className="text-white/60 text-sm">Immer verfügbar</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-center gap-2 text-orange-400 font-bold text-sm uppercase tracking-wider">
                      <SafeIcon icon={FiExternalLink} className="text-lg" />
                      Klicken zum Starten
                      <SafeIcon icon={FiArrowRight} className="text-lg" />
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Grid - Nike Style */}
        <section className="py-20 lg:py-32 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{opacity: 0,y: 30}}
              whileInView={{opacity: 1,y: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8}}
            >
              <h2 className="text-4xl lg:text-6xl font-black text-white mb-4">
                WARUM <span className="text-orange-500">WARTEN?</span>
              </h2>
              <p className="text-xl text-white/60 font-light">
                Hol dir die Antworten, die du brauchst. Sofort.
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-3 gap-8">
              <motion.div 
                className="group bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 transition-all hover:bg-white/10 hover:scale-105 hover:border-orange-500/30"
                initial={{opacity: 0,y: 50}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.8,delay: 0.1}}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={FiZap} className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-black text-white mb-4 uppercase tracking-tight">Schnell</h3>
                <p className="text-white/60 font-light leading-relaxed">
                  Kein Warten mehr auf Antworten von Menschen. Sekundenschnelle Lösungen.
                </p>
              </motion.div>

              <motion.div 
                className="group bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 transition-all hover:bg-white/10 hover:scale-105 hover:border-orange-500/30"
                initial={{opacity: 0,y: 50}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.8,delay: 0.2}}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={FiCheck} className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-black text-white mb-4 uppercase tracking-tight">Immer</h3>
                <p className="text-white/60 font-light leading-relaxed">
                  24/7 verfügbar für alle deine Fragen. Kein Schlaf, keine Pausen.
                </p>
              </motion.div>

              <motion.div 
                className="group bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10 transition-all hover:bg-white/10 hover:scale-105 hover:border-orange-500/30"
                initial={{opacity: 0,y: 50}}
                whileInView={{opacity: 1,y: 0}}
                viewport={{once: true}}
                transition={{duration: 0.8,delay: 0.3}}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={FiUsers} className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-black text-white mb-4 uppercase tracking-tight">Community</h3>
                <p className="text-white/60 font-light leading-relaxed">
                  Lerne von Experten und tausche dich mit anderen KI-Enthusiasten aus.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section - Nike Style */}
        <section className="py-20 lg:py-32 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{opacity: 0,scale: 0.9}}
              whileInView={{opacity: 1,scale: 1}}
              viewport={{once: true}}
              transition={{duration: 0.8}}
            >
              <h2 className="text-4xl lg:text-6xl font-black text-white mb-8">
                BIST DU <span className="text-orange-500">BEREIT?</span>
              </h2>
              <p className="text-xl text-white/60 font-light mb-12">
                Egal ob Creator, Unternehmer oder Marketing-Profi – bei "KI. MACH ES EINFACH" bekommst du alles, was du brauchst, um Marketing neu zu denken: Know-how, Tools und eine starke Community, die dich auf dem Weg zum Erfolg unterstützt und antreibt. Lerne JETZT, wie man KI richtig nutzt und zum absoluten PRO wird.
              </p>
              
              <motion.button 
                onClick={handleCommunityClick}
                className="group relative bg-gradient-to-r from-orange-500 to-red-600 text-white px-16 py-6 rounded-full font-black text-lg uppercase tracking-wider transition-all hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/25 inline-flex items-center gap-3 mb-6"
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
              >
                <span>Zur KI Community</span>
                <SafeIcon icon={FiArrowRight} className="text-xl group-hover:translate-x-2 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity -z-10"></div>
              </motion.button>
              
              <p className="text-white/40 text-sm">
                Werde Teil der kostenlosen Community rund um das Thema Künstliche Intelligenz
              </p>
            </motion.div>
          </div>
        </section>

        {/* Success Message - Nike Style */}
        <section className="py-20 lg:py-32 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{opacity: 0,y: 50}}
              whileInView={{opacity: 1,y: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8}}
            >
              <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500/30 px-4 py-2 rounded-full mb-8">
                <SafeIcon icon={FiCheck} className="text-green-400 text-lg" />
                <span className="text-green-400 font-bold text-sm uppercase tracking-wider">Mission Complete</span>
              </div>
              
              <h2 className="text-4xl lg:text-6xl font-black text-white mb-6">
                Nächstes Mal<br/><span className="text-green-400">besser.</span>
              </h2>
              <p className="text-xl text-white/60 font-light">
                ChatGPT ist kostenlos verfügbar und kann dir bei fast allen Fragen helfen. 
                Nutze die Macht der KI.
              </p>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 bg-black/50 backdrop-blur-xl border-t border-white/10 px-6 lg:px-8 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center space-y-8 text-center">
            <div className="flex items-center gap-3 text-orange-400">
              <SafeIcon icon={FiAlertTriangle} className="text-xl" />
              <span className="font-black text-lg uppercase tracking-wider">Satire Projekt</span>
            </div>
            
            <p className="text-white/60 leading-relaxed max-w-2xl font-light">
              Dieses glorreiche Meisterwerk der digitalen Erziehung ist und war ein reines <span className="text-orange-400 font-bold">Satire-Projekt</span>. 
              Es entsprang den kreativen (und leicht wahnsinnigen) Köpfen von <span className="text-white font-bold">Gernot Pirker</span> sowie <span className="text-white font-bold">Michael Schirnhofer</span>.
            </p>
            
            <p className="text-white/40 italic text-sm max-w-md">
              Wir wollten nur sicherstellen, dass du heute wenigstens einmal schmunzelst – und den Weg zur KI endlich findest!
            </p>

            <a 
              href="https://monkeymedia.at" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white/60 hover:text-orange-400 transition-colors font-bold text-sm uppercase tracking-wider border-b border-transparent hover:border-orange-400 pb-0.5"
            >
              Impressum @ MonkeyMedia.at
            </a>

            <Link 
              to="/admin" 
              className="text-white/20 hover:text-white transition-colors text-xs font-bold uppercase tracking-wider flex items-center gap-2 mt-4"
            >
              <SafeIcon icon={FiLock} className="text-sm" />
              <span>Admin Dashboard</span>
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;